import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDecisions, DecisionMode } from '../lib/stores/decisions';
import Card from '../lib/design-system/components/Card';
import Button from '../lib/design-system/components/Button';

type DecisionStatus = 'in-progress' | 'decided' | 'implemented';
type FilterType = 'all' | DecisionStatus | DecisionMode;

export default function DecisionsPage() {
  const navigate = useNavigate();
  const { decisions, loading, error, initialize, createDecision, deleteDecision, initialized } = useDecisions();

  const [filterType, setFilterType] = useState<FilterType>('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    mode: 'pros-cons' as DecisionMode,
  });

  useEffect(() => {
    if (!initialized) initialize();
  }, [initialized]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.title.trim()) {
      const newDecision = await createDecision({
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        mode: formData.mode,
      });
      if (newDecision) {
        navigate(`/tools/decisions/${newDecision.id}`);
      }
    }
  };

  const filteredDecisions = decisions.filter(d => {
    if (filterType === 'all') return true;
    if (filterType === 'pros-cons' || filterType === 'matrix') return d.mode === filterType;
    return d.status === filterType;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'decided': return 'var(--color-success-600)';
      case 'implemented': return 'var(--color-primary-600)';
      default: return 'var(--color-warning-600)';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in-progress': return 'In Progress';
      case 'decided': return 'Decided';
      case 'implemented': return 'Implemented';
      default: return status;
    }
  };

  const getModeLabel = (mode: DecisionMode) => {
    return mode === 'pros-cons' ? 'Pros & Cons' : 'Decision Matrix';
  };

  if (loading && !initialized) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading decisions...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', width: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '2.5rem',
          fontWeight: 400,
          color: 'var(--color-neutral-900)',
          marginBottom: '0.5rem',
        }}>
          Decisions
        </h1>
        <p style={{ color: 'var(--color-neutral-600)', fontSize: '1.125rem' }}>
          Make better decisions using structured frameworks
        </p>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#DC2626',
          marginBottom: '1.5rem',
        }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
      }}>
        <button
          onClick={() => setFilterType('all')}
          style={{
            padding: '0.5rem 1rem',
            border: filterType === 'all' ? '2px solid var(--color-primary-600)' : '1px solid var(--color-neutral-300)',
            borderRadius: '8px',
            background: filterType === 'all' ? 'var(--color-primary-50)' : 'white',
            color: filterType === 'all' ? 'var(--color-primary-700)' : 'var(--color-neutral-700)',
            fontWeight: filterType === 'all' ? 600 : 400,
            cursor: 'pointer',
          }}
        >
          All ({decisions.length})
        </button>

        <div style={{ width: '1px', height: '32px', backgroundColor: 'var(--color-neutral-300)' }} />

        {(['in-progress', 'decided', 'implemented'] as DecisionStatus[]).map(status => (
          <button
            key={status}
            onClick={() => setFilterType(status)}
            style={{
              padding: '0.5rem 1rem',
              border: filterType === status ? `2px solid ${getStatusColor(status)}` : '1px solid var(--color-neutral-300)',
              borderRadius: '8px',
              background: filterType === status ? `${getStatusColor(status)}15` : 'white',
              color: filterType === status ? getStatusColor(status) : 'var(--color-neutral-700)',
              fontWeight: filterType === status ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {getStatusLabel(status)}
          </button>
        ))}

        <div style={{ width: '1px', height: '32px', backgroundColor: 'var(--color-neutral-300)' }} />

        {(['pros-cons', 'matrix'] as DecisionMode[]).map(mode => (
          <button
            key={mode}
            onClick={() => setFilterType(mode)}
            style={{
              padding: '0.5rem 1rem',
              border: filterType === mode ? '2px solid var(--color-primary-600)' : '1px solid var(--color-neutral-300)',
              borderRadius: '8px',
              background: filterType === mode ? 'var(--color-primary-50)' : 'white',
              color: filterType === mode ? 'var(--color-primary-700)' : 'var(--color-neutral-700)',
              fontWeight: filterType === mode ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {getModeLabel(mode)}
          </button>
        ))}
      </div>

      {/* Create Button */}
      <div style={{ marginBottom: '1.5rem' }}>
        <Button onClick={() => setShowCreateForm(!showCreateForm)}>
          {showCreateForm ? 'Cancel' : '+ New Decision'}
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card style={{ marginBottom: '2rem', padding: '1.5rem' }}>
          <form onSubmit={handleCreate}>
            <h3 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '1.5rem',
              marginBottom: '1rem',
            }}>
              Create New Decision
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--color-neutral-700)',
              }}>
                Decision Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="e.g., Choose new project management tool"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--color-neutral-700)',
              }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                placeholder="Additional context or criteria..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                fontWeight: 600,
                marginBottom: '0.75rem',
                color: 'var(--color-neutral-700)',
              }}>
                Decision Framework *
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, mode: 'pros-cons' })}
                  style={{
                    padding: '1rem',
                    border: formData.mode === 'pros-cons' ? '2px solid var(--color-primary-600)' : '1px solid var(--color-neutral-300)',
                    borderRadius: '8px',
                    background: formData.mode === 'pros-cons' ? 'var(--color-primary-50)' : 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-neutral-900)' }}>
                    Pros & Cons
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-neutral-600)' }}>
                    Simple qualitative comparison
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, mode: 'matrix' })}
                  style={{
                    padding: '1rem',
                    border: formData.mode === 'matrix' ? '2px solid var(--color-primary-600)' : '1px solid var(--color-neutral-300)',
                    borderRadius: '8px',
                    background: formData.mode === 'matrix' ? 'var(--color-primary-50)' : 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-neutral-900)' }}>
                    Decision Matrix
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-neutral-600)' }}>
                    Weighted scoring across criteria
                  </div>
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button type="submit" disabled={!formData.title.trim()}>
                Create Decision
              </Button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* Decisions List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {filteredDecisions.length === 0 ? (
          <Card style={{ padding: '3rem', textAlign: 'center' }}>
            <p style={{ color: 'var(--color-neutral-500)' }}>
              {filterType === 'all'
                ? 'No decisions yet. Create your first decision to get started!'
                : `No ${filterType} decisions.`}
            </p>
          </Card>
        ) : (
          filteredDecisions.map(decision => (
            <Card
              key={decision.id}
              style={{
                padding: '1.5rem',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s',
              }}
              onClick={() => navigate(`/tools/decisions/${decision.id}`)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    color: 'var(--color-neutral-900)',
                  }}>
                    {decision.title}
                  </h3>
                  {decision.description && (
                    <p style={{
                      color: 'var(--color-neutral-600)',
                      marginBottom: '0.75rem',
                      fontSize: '0.875rem',
                    }}>
                      {decision.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: `${getStatusColor(decision.status)}15`,
                      color: getStatusColor(decision.status),
                    }}>
                      {getStatusLabel(decision.status)}
                    </span>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      backgroundColor: 'var(--color-neutral-100)',
                      color: 'var(--color-neutral-700)',
                    }}>
                      {getModeLabel(decision.mode)}
                    </span>
                    <span style={{
                      fontSize: '0.75rem',
                      color: 'var(--color-neutral-500)',
                    }}>
                      Created {new Date(decision.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm('Delete this decision?')) {
                      deleteDecision(decision.id);
                    }
                  }}
                  style={{
                    padding: '0.25rem 0.5rem',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--color-error-600)',
                    cursor: 'pointer',
                    fontSize: '1.25rem',
                  }}
                  title="Delete decision"
                >
                  ×
                </button>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Framework Explanation */}
      <Card style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: 'var(--color-primary-50)' }}>
        <h3 style={{ marginBottom: '0.75rem', fontWeight: 600 }}>Choosing a Framework</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <div>
            <h4 style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Pros & Cons
            </h4>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-neutral-700)', lineHeight: '1.6' }}>
              Best for simple decisions with qualitative factors. List advantages and disadvantages
              for each option to see which has the stronger case.
            </p>
          </div>
          <div>
            <h4 style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Decision Matrix
            </h4>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-neutral-700)', lineHeight: '1.6' }}>
              Best for complex decisions with multiple criteria. Score each option against weighted
              criteria for an objective, numerical comparison.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
